// AdBrain.jsx — Meta ads judged by what they bring into the clinic.
//
// Spend comes from meta-ads-sync (Marketing API). Outcomes come from Elyon:
// lead (meta_ad_id) -> client -> completed visit -> payments within 90 days.
// The daily analyst (ad-brain) writes recommendations; the owner approves or
// rejects them here, and approved ones run through ad-brain-execute, which
// enforces the guardrails server-side. Autopilot is off until the owner turns
// it on in the settings card below.
//
// Visible to anyone who can view sales; only admin/owner can approve or change
// settings (also enforced in SQL and in the edge function).
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "./supabase";
import { useAuth } from "./AuthContext";

const C = {
  bg: "#f8f5f0", card: "#ffffff", border: "#e8e4df", gold: "#c9a84c", goldD: "#8a6f2e", goldBg: "rgba(201,168,76,0.08)",
  text: "#1a1a2e", muted: "#6b7280", dim: "#9ca3af",
  green: "#1f9d63", greenL: "rgba(46,204,135,0.12)", red: "#d14343", redL: "rgba(224,85,85,0.10)",
  amber: "#b7791f", amberL: "rgba(245,158,11,0.12)", blue: "#2f6fd1", blueL: "rgba(74,158,255,0.10)",
};
const F = "'Montserrat','Plus Jakarta Sans',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const money = (n, d = 0) => (n === null || n === undefined || Number.isNaN(n)) ? "—"
  : "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
const ymd = (d) => d.toISOString().slice(0, 10);
const ago = (iso) => {
  if (!iso) return "never";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${m} min ago`;
  if (m < 48 * 60) return `${Math.round(m / 60)} h ago`;
  return `${Math.round(m / 1440)} days ago`;
};
const ACTION_LABEL = { pause_ad: "Pause ad", resume_ad: "Resume ad", change_budget: "Change budget", new_creative: "New creative", note: "Note" };
const STATUS_STYLE = {
  pending: [C.amber, C.amberL], approved: [C.blue, C.blueL], executed: [C.green, C.greenL], auto_executed: [C.green, C.greenL],
  rejected: [C.muted, "#f3f4f6"], failed: [C.red, C.redL], expired: [C.dim, "#f3f4f6"],
};

const card = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18 };
const btn = (primary, disabled) => ({
  padding: "8px 14px", fontSize: 12, fontWeight: 700, fontFamily: F, borderRadius: 8, cursor: disabled ? "default" : "pointer",
  border: `1px solid ${primary ? C.gold : C.border}`, background: primary ? C.gold : "#fff", color: primary ? "#1a1a2e" : C.text,
  opacity: disabled ? 0.5 : 1,
});
const th = { textAlign: "left", padding: "8px 10px", fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", color: C.muted, fontWeight: 700, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" };
const td = { padding: "8px 10px", fontSize: 12, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" };
const num = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

function Pill({ status }) {
  const [fg, bg] = STATUS_STYLE[status] || [C.muted, "#f3f4f6"];
  return <span style={{ fontSize: 10, fontWeight: 700, color: fg, background: bg, padding: "3px 8px", borderRadius: 20, textTransform: "uppercase", letterSpacing: "0.05em" }}>{status.replace("_", " ")}</span>;
}

function Stat({ label, value, sub, tone }) {
  const color = tone === "bad" ? C.red : tone === "good" ? C.green : C.text;
  return (
    <div style={{ ...card, padding: 14, minWidth: 0 }}>
      <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function AdBrain() {
  const { profile, canViewSales } = useAuth();
  const isOwner = ["admin", "owner"].includes(String(profile?.role || "").toLowerCase().trim());

  const [days, setDays] = useState(30);
  const [rows, setRows] = useState([]);
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [flash, setFlash] = useState("");
  const [draft, setDraft] = useState(null);
  const [confirmAuto, setConfirmAuto] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    const to = new Date(), from = new Date(Date.now() - (days - 1) * 86400000);
    const [f, s] = await Promise.all([
      supabase.rpc("ad_funnel_by_ad", { p_from: ymd(from), p_to: ymd(to) }),
      supabase.rpc("ad_brain_state"),
    ]);
    if (f.error || s.error) setErr((f.error || s.error).message);
    setRows(f.data || []);
    setState(s.data || null);
    if (s.data?.settings) setDraft((d) => d || s.data.settings);
    setLoading(false);
  }, [days]);

  useEffect(() => { if (canViewSales) load(); }, [load, canViewSales]);

  const invoke = async (fn, body, label) => {
    setBusy(label); setFlash("");
    const { data, error } = await supabase.functions.invoke(fn, { body });
    setBusy("");
    if (error || data?.ok === false) {
      let msg = data?.error || data?.message || data?.reason || error?.message || "Something went wrong";
      try { const j = await error?.context?.json?.(); msg = j?.error || j?.message || j?.reason || msg; } catch { /* keep msg */ }
      setFlash(`⚠ ${msg}`);
    }
    await load();
    return data;
  };

  const totals = useMemo(() => {
    const t = rows.reduce((a, r) => ({
      spend: a.spend + Number(r.spend || 0), leads: a.leads + Number(r.crm_leads || 0), booked: a.booked + Number(r.booked || 0),
      showed: a.showed + Number(r.showed || 0), paid: a.paid + Number(r.paid || 0), rev: a.rev + Number(r.revenue_90d || 0),
    }), { spend: 0, leads: 0, booked: 0, showed: 0, paid: 0, rev: 0 });
    return t;
  }, [rows]);

  const campaigns = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const key = r.campaign_id || "none";
      if (!m.has(key)) m.set(key, { id: key, name: r.campaign_name || (r.ad_id ? "Unknown campaign" : "Leads with no ad id"), ads: [] });
      m.get(key).ads.push(r);
    }
    return [...m.values()].map((c) => ({ ...c, spend: c.ads.reduce((s, a) => s + Number(a.spend || 0), 0) }))
      .sort((a, b) => b.spend - a.spend);
  }, [rows]);

  if (!canViewSales) {
    return <div style={{ ...card, color: C.muted, fontFamily: F }}>Ad performance includes revenue, so it is visible only to people who can view sales.</div>;
  }

  const target = Number(state?.settings?.target_cost_per_paid || 250);
  const cpp = totals.paid ? totals.spend / totals.paid : null;
  const recs = state?.recommendations || [];
  const pending = recs.filter((r) => r.status === "pending");
  const decided = recs.filter((r) => r.status !== "pending").slice(0, 12);
  const lastRun = (state?.runs || [])[0];
  const budgets = (state?.objects || []).filter((o) => o.effective_status === "ACTIVE" && o.daily_budget);
  const budgetTotal = budgets.reduce((s, o) => s + Number(o.daily_budget || 0), 0);

  const cppTone = (spend, paid) => (!paid ? (spend > target ? "bad" : null) : (spend / paid <= target ? "good" : "bad"));
  const toneColor = (t) => (t === "good" ? C.green : t === "bad" ? C.red : C.text);

  const row = (r, indent, key, style) => {
    const tone = cppTone(Number(r.spend), Number(r.paid));
    const roas = Number(r.spend) > 0 ? Number(r.revenue_90d) / Number(r.spend) : null;
    return (
      <tr key={key || (r.ad_id || "none")} style={style}>
        <td style={{ ...td, paddingLeft: indent ? 26 : 10, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }} title={r.ad_name || ""}>
          {r.ad_name || (r.ad_id ? r.ad_id : "No ad id on lead")}
          {r.ad_status && r.ad_status !== "ACTIVE" && <span style={{ marginLeft: 6, fontSize: 9, color: C.dim }}>{r.ad_status}</span>}
        </td>
        <td style={num}>{money(r.spend)}</td>
        <td style={num}>{Number(r.crm_leads)}</td>
        <td style={num}>{Number(r.crm_leads) ? money(Number(r.spend) / Number(r.crm_leads), 2) : "—"}</td>
        <td style={num}>{Number(r.booked)}</td>
        <td style={num}>{Number(r.showed)}</td>
        <td style={num}>{Number(r.paid)}</td>
        <td style={{ ...num, fontWeight: 700, color: toneColor(tone) }}>{Number(r.paid) ? money(Number(r.spend) / Number(r.paid)) : (Number(r.spend) ? "none yet" : "—")}</td>
        <td style={num}>{money(r.revenue_90d)}</td>
        <td style={num}>{roas === null ? "—" : roas.toFixed(1) + "x"}</td>
      </tr>
    );
  };

  return (
    <div style={{ fontFamily: F, color: C.text, display: "grid", gap: 18 }}>
      {/* Header bar */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        <div style={{ fontSize: 12, color: C.muted }}>
          Spend synced {ago(state?.last_sync)} · Brain last ran {ago(lastRun?.created_at)}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} style={{ ...btn(days === d), padding: "6px 10px" }}>{d} days</button>
          ))}
          <button disabled={!!busy} onClick={() => invoke("meta-ads-sync", { days: 120 }, "sync")} style={btn(false, !!busy)}>
            {busy === "sync" ? "Syncing…" : "Sync spend now"}
          </button>
          {isOwner && (
            <button disabled={!!busy} onClick={() => invoke("ad-brain", { trigger: "manual" }, "brain")} style={btn(true, !!busy)}>
              {busy === "brain" ? "Analysing (about a minute)…" : "Run analysis now"}
            </button>
          )}
        </div>
      </div>
      {flash && <div style={{ ...card, padding: 10, background: C.redL, borderColor: C.redL, fontSize: 12 }}>{flash}</div>}
      {err && <div style={{ ...card, padding: 10, background: C.redL, borderColor: C.redL, fontSize: 12 }}>Could not load: {err}</div>}

      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        <Stat label={`Spend · ${days}d`} value={money(totals.spend)} sub={budgets.length ? `${money(budgetTotal)}/day active budgets` : null} />
        <Stat label="Leads" value={totals.leads} sub={totals.leads ? `${money(totals.spend / totals.leads, 2)} per lead` : null} />
        <Stat label="Booked → Showed" value={`${totals.booked} → ${totals.showed}`} sub={totals.leads ? `${Math.round(totals.showed / totals.leads * 100)}% of leads showed` : null} />
        <Stat label="Paying patients" value={totals.paid} sub="paid within 90 days of the lead" />
        <Stat label="Cost per paying patient" value={cpp ? money(cpp) : "—"} sub={`target ${money(target)}`} tone={cpp ? (cpp <= target ? "good" : "bad") : null} />
        <Stat label="90-day revenue" value={money(totals.rev)} sub={totals.spend ? `${(totals.rev / totals.spend).toFixed(1)}x return on spend` : null} />
      </div>
      <div style={{ fontSize: 11, color: C.dim, marginTop: -8 }}>
        Leads from the last 2–3 weeks are still maturing: many have not had time to book, show or pay, so recent ads look worse than they will.
      </div>

      {/* Brain */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 14, alignItems: "start" }}>
        <div style={{ ...card, display: "grid", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <div style={{ fontSize: 18, fontWeight: 800, fontFamily: S }}>Today's recommendations</div>
            <div style={{ fontSize: 11, color: C.muted }}>{pending.length} waiting</div>
          </div>
          {lastRun?.summary && (
            <div dir="auto" style={{ fontSize: 13, lineHeight: 1.6, background: C.goldBg, border: `1px solid rgba(201,168,76,0.25)`, borderRadius: 8, padding: 12, whiteSpace: "pre-wrap" }}>
              {lastRun.summary}
            </div>
          )}
          {lastRun?.status === "error" && <div style={{ fontSize: 12, color: C.red }}>Last run failed: {lastRun.error}</div>}
          {lastRun?.status === "skipped" && <div style={{ fontSize: 12, color: C.amber }}>{lastRun.summary}</div>}
          {!pending.length && <div style={{ fontSize: 12, color: C.muted }}>Nothing waiting for a decision. The analyst runs every morning at 8:40.</div>}
          {pending.map((r) => (
            <div key={r.id} style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, display: "grid", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: C.goldD, textTransform: "uppercase", letterSpacing: "0.05em" }}>{ACTION_LABEL[r.action]}</span>
                {r.priority === 1 && <span style={{ fontSize: 10, fontWeight: 700, color: C.red }}>TODAY</span>}
                <span style={{ fontSize: 12, fontWeight: 600 }}>{r.target_name}</span>
                {r.action === "change_budget" && (
                  <span style={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}>{money(r.params?.old_daily_budget)} → <b>{money(r.params?.new_daily_budget)}</b>/day</span>
                )}
              </div>
              <div dir="auto" style={{ fontSize: 13, lineHeight: 1.55 }}>{r.reason}</div>
              {r.expected_impact && <div dir="auto" style={{ fontSize: 12, color: C.muted }}>{r.expected_impact}</div>}
              {r.action === "new_creative" && (
                <div style={{ background: "#fafafa", borderRadius: 8, padding: 10, fontSize: 12, display: "grid", gap: 4 }}>
                  {r.params?.angle && <div style={{ color: C.muted }}>Angle: {r.params.angle}</div>}
                  <div><b>{r.params?.headline}</b></div>
                  <div style={{ whiteSpace: "pre-wrap" }}>{r.params?.primary_text}</div>
                  {r.params?.visual_brief && <div style={{ color: C.muted }}>Visual: {r.params.visual_brief}</div>}
                  <div>
                    <button style={{ ...btn(false), padding: "4px 10px", fontSize: 11 }}
                      onClick={() => navigator.clipboard?.writeText(`${r.params?.headline}\n\n${r.params?.primary_text}`).then(() => setFlash("Ad copy copied."), () => setFlash("Select the text to copy it."))}>
                      Copy ad text
                    </button>
                  </div>
                </div>
              )}
              {isOwner ? (
                <div style={{ display: "flex", gap: 6 }}>
                  <button disabled={!!busy} style={btn(true, !!busy)} onClick={() => invoke("ad-brain-execute", { recommendation_id: r.id }, `rec${r.id}`)}>
                    {busy === `rec${r.id}` ? "Working…" : r.action === "new_creative" || r.action === "note" ? "Approve" : "Approve & apply in Meta"}
                  </button>
                  <button disabled={!!busy} style={btn(false, !!busy)} onClick={async () => {
                    setBusy(`rej${r.id}`); const { error } = await supabase.rpc("ad_brain_reject", { p_id: r.id }); setBusy("");
                    if (error) setFlash(`⚠ ${error.message}`); load();
                  }}>Reject</button>
                </div>
              ) : <div style={{ fontSize: 11, color: C.dim }}>Waiting for the owner's decision.</div>}
            </div>
          ))}
          {!!decided.length && (
            <div style={{ display: "grid", gap: 6, marginTop: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Recent decisions</div>
              {decided.map((r) => (
                <div key={r.id} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, flexWrap: "wrap" }}>
                  <Pill status={r.status} />
                  <span>{ACTION_LABEL[r.action]}</span>
                  <span style={{ color: C.muted }}>{r.target_name}</span>
                  {r.result?.message && <span style={{ color: r.status === "failed" ? C.red : C.dim }}>· {r.result.message}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Settings */}
        <div style={{ ...card, display: "grid", gap: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 800, fontFamily: S }}>Guardrails</div>
          {draft && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, borderRadius: 8, background: draft.autopilot_enabled ? C.greenL : "#f6f6f6" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>Autopilot {draft.autopilot_enabled ? "ON" : "OFF"}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>When on, the morning run may pause losing ads and move budgets by itself, within the limits below. New creatives always wait for you.</div>
                </div>
                {isOwner && !confirmAuto && (
                  <button style={btn(!draft.autopilot_enabled)} onClick={() => draft.autopilot_enabled ? setDraft({ ...draft, autopilot_enabled: false }) : setConfirmAuto(true)}>
                    {draft.autopilot_enabled ? "Turn off" : "Turn on"}
                  </button>
                )}
              </div>
              {confirmAuto && (
                <div style={{ border: `1px solid ${C.amber}`, borderRadius: 8, padding: 10, fontSize: 12, display: "grid", gap: 8 }}>
                  <div>Autopilot will change live ads and budgets in Meta without asking, up to {money(draft.max_total_daily_budget)}/day in total and {draft.max_budget_change_pct}% per change. Turn it on?</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button style={btn(true)} onClick={() => { setDraft({ ...draft, autopilot_enabled: true }); setConfirmAuto(false); }}>Yes, turn on (then Save)</button>
                    <button style={btn(false)} onClick={() => setConfirmAuto(false)}>Cancel</button>
                  </div>
                </div>
              )}
              {[
                ["target_cost_per_paid", "Target cost per paying patient ($)"],
                ["max_total_daily_budget", "Max total daily budget ($)"],
                ["max_budget_change_pct", "Max budget change per step (%)"],
                ["min_spend_to_judge", "Min spend before an ad can be paused ($)"],
              ].map(([k, label]) => (
                <label key={k} style={{ display: "grid", gridTemplateColumns: "1fr 110px", alignItems: "center", gap: 10, fontSize: 12 }}>
                  <span>{label}</span>
                  <input id={`adbrain-${k}`} type="number" disabled={!isOwner} value={draft[k] ?? ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                    style={{ padding: "6px 8px", border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: F, fontSize: 12, textAlign: "right" }} />
                </label>
              ))}
              <label style={{ display: "grid", gap: 6, fontSize: 12 }}>
                <span>What the analyst must know about the business</span>
                <textarea id="adbrain-context" dir="auto" rows={5} disabled={!isOwner} value={draft.business_context || ""} onChange={(e) => setDraft({ ...draft, business_context: e.target.value })}
                  style={{ padding: 8, border: `1px solid ${C.border}`, borderRadius: 6, fontFamily: F, fontSize: 12, resize: "vertical" }} />
              </label>
              {isOwner && (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button disabled={!!busy} style={btn(true, !!busy)} onClick={async () => {
                    setBusy("save");
                    const { data, error } = await supabase.rpc("ad_brain_save_settings", { p: draft });
                    setBusy("");
                    if (error) setFlash(`⚠ ${error.message}`); else { setDraft(data); setFlash("Guardrails saved."); }
                    load();
                  }}>{busy === "save" ? "Saving…" : "Save"}</button>
                  {state?.settings?.updated_by && <span style={{ fontSize: 11, color: C.dim }}>Last changed by {state.settings.updated_by} {ago(state.settings.updated_at)}</span>}
                </div>
              )}
            </>
          )}
          {!!(state?.actions || []).length && (
            <div style={{ display: "grid", gap: 4, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Changes sent to Meta</div>
              {state.actions.slice(0, 8).map((a) => (
                <div key={a.id} style={{ fontSize: 11, color: a.ok ? C.text : C.red }}>
                  {new Date(a.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} · {a.actor} · {a.response}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Funnel table */}
      <div style={{ ...card, padding: 0, overflowX: "auto" }}>
        <div style={{ padding: "14px 18px 8px", display: "flex", alignItems: "baseline", gap: 10 }}>
          <div style={{ fontSize: 18, fontWeight: 800, fontFamily: S }}>What each ad brought in</div>
          <div style={{ fontSize: 11, color: C.muted }}>leads created in the last {days} days · revenue = what they paid within 90 days</div>
        </div>
        {loading ? <div style={{ padding: 18, fontSize: 12, color: C.muted }}>Loading…</div>
          : !rows.length ? (
            <div style={{ padding: 18, fontSize: 12, color: C.muted }}>
              No spend data yet. Press "Sync spend now" to pull the last 120 days from Meta.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead><tr>
                <th style={th}>Campaign / ad</th><th style={{ ...th, textAlign: "right" }}>Spend</th><th style={{ ...th, textAlign: "right" }}>Leads</th>
                <th style={{ ...th, textAlign: "right" }}>Per lead</th><th style={{ ...th, textAlign: "right" }}>Booked</th><th style={{ ...th, textAlign: "right" }}>Showed</th>
                <th style={{ ...th, textAlign: "right" }}>Paid</th><th style={{ ...th, textAlign: "right" }}>Per paying patient</th>
                <th style={{ ...th, textAlign: "right" }}>Revenue 90d</th><th style={{ ...th, textAlign: "right" }}>Return</th>
              </tr></thead>
              <tbody>
                {campaigns.map((c) => {
                  const sum = c.ads.reduce((a, r) => {
                    for (const k of ["spend", "crm_leads", "booked", "showed", "paid", "revenue_90d"]) a[k] = (a[k] || 0) + Number(r[k] || 0);
                    return a;
                  }, {});
                  return [
                    row({ ...sum, ad_name: c.name, ad_id: c.id }, false, `c-${c.id}`, { background: C.goldBg, fontWeight: 700 }),
                    ...[...c.ads].sort((a, b) => Number(b.spend) - Number(a.spend)).map((r) => row(r, true, `a-${c.id}-${r.ad_id || "none"}`)),
                  ];
                })}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}
